import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

const ManagerAuthentication = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    emailOrPhone: '',
    password: '',
    language: 'en'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const languageOptions = [
    { value: 'en', label: 'English - English' },
    { value: 'hi', label: 'हिन्दी - Hindi' },
    { value: 'mr', label: 'मराठी - Marathi' },
    { value: 'te', label: 'తెలుగు - Telugu' },
    { value: 'kn', label: 'ಕನ್ನಡ - Kannada' },
    { value: 'ta', label: 'தமிழ் - Tamil' },
    { value: 'gu', label: 'ગુજરાતી - Gujarati' }
  ];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (!savedLanguage) {
      localStorage.setItem('selectedLanguage', 'en');
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.emailOrPhone?.trim()) {
      newErrors.emailOrPhone = 'Email or phone number is required';
    } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/?.test(formData?.emailOrPhone) && 
               !/^[6-9]\d{9}$/?.test(formData?.emailOrPhone)) {
      newErrors.emailOrPhone = 'Please enter a valid email or 10-digit phone number';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleManagerLogin = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    setTimeout(() => {
      setErrors({
        emailOrPhone: 'Invalid credentials. Please contact your administrator.',
        password: 'Invalid credentials. Please contact your administrator.'
      });
      setIsLoading(false);
    }, 1500);
  };

  return (
    <>
      <Helmet>
        <title>Manager Login - SugarHathi | Operational Management Access</title>
        <meta name="description" content="Manager access to SugarHathi platform for sub-administrative oversight and operational management. Secure login for managers with limited privileges." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex flex-col items-center justify-center px-4 py-8">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-[#1e4a72]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-[#ff8c00]/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 w-full max-w-md">
          <form onSubmit={handleManagerLogin} className="w-full space-y-6 bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-xl border border-gray-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-4 overflow-hidden bg-gradient-to-br from-blue-50 to-orange-50 shadow-lg ring-2 ring-[#1e4a72]/20 cursor-pointer" onClick={() => navigate('/')} title="SugarHathi home">
                <img
                  src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png"
                  alt="SugarHathi logo - Blue elephant mascot for manager operational access"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] bg-clip-text text-transparent mb-2">
                Manager Access
              </h1>
              <p className="text-muted-foreground">Operational Management Portal</p>
              <div className="mt-3 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-100">
                <Icon name="Shield" size={16} className="text-[#1e4a72]" />
                <span className="text-xs text-[#1e4a72] font-medium">Sub-Administrative Privileges</span>
              </div>
            </div>

            <div className="space-y-4">
              <Input
                label="Manager Email or Phone"
                type="text"
                placeholder="Enter manager email or phone"
                value={formData?.emailOrPhone}
                onChange={(e) => handleInputChange('emailOrPhone', e?.target?.value)}
                error={errors?.emailOrPhone}
                required
              />

              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  error={errors?.password}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-[#ff8c00] transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              <Select
                label="Preferred Language"
                options={languageOptions}
                value={formData?.language}
                onChange={(value) => handleInputChange('language', value)}
                placeholder="Select your language"
              />
            </div>

            <Button
              type="submit"
              variant="default"
              size="lg"
              fullWidth
              loading={isLoading}
              iconName="LogIn"
              iconPosition="right"
              className="bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] hover:opacity-90 text-white shadow-lg"
            >
              {isLoading ? 'Authenticating...' : 'Manager Login'}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-[#1e4a72] hover:text-[#ff8c00] font-medium transition-colors"
              >
                Forgot Password?
              </button>
              <button
                type="button"
                onClick={() => navigate('/login-authentication')}
                className="text-[#1e4a72] hover:text-[#ff8c00] font-medium transition-colors flex items-center gap-1"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Back to Login</span>
              </button>
            </div>
          </form>

          {/* Trust Signals */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-lg text-center border border-gray-100">
              <Icon name="Shield" size={24} className="mx-auto text-[#1e4a72] mb-2" />
              <p className="text-xs text-muted-foreground">Secure Access</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-lg text-center border border-gray-100">
              <Icon name="UserCheck" size={24} className="mx-auto text-[#ff8c00] mb-2" />
              <p className="text-xs text-muted-foreground">Role-Based</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-4 rounded-lg text-center border border-gray-100">
              <Icon name="Eye" size={24} className="mx-auto text-[#1e4a72] mb-2" />
              <p className="text-xs text-muted-foreground">Limited Scope</p>
            </div>
          </div>
        </div>

        <footer className="relative z-10 mt-12 text-center">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date()?.getFullYear()} SugarHathi. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <a href="/privacy-policy" className="text-xs text-[#1e4a72] hover:text-[#ff8c00] transition-colors">Privacy Policy</a>
            <span className="text-xs text-muted-foreground">•</span>
            <a href="/terms" className="text-xs text-[#1e4a72] hover:text-[#ff8c00] transition-colors">Terms of Service</a>
            <span className="text-xs text-muted-foreground">•</span>
            <a href="/support" className="text-xs text-[#1e4a72] hover:text-[#ff8c00] transition-colors">Support</a>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ManagerAuthentication;